import type { Map as MaplibreMap } from "maplibre-gl";
import type { PrintLayerAdapter } from "./printLayerAdapterRegistry";
import type { PrintRenderContext } from "./printRenderScale";

export interface MaplibrePrintLayerDefinition {
  id: string;
  visible: boolean;
  layoutPixelProperties?: readonly string[];
  paintPixelProperties?: readonly string[];
}

// 相対単位はtext-size等に追従するため、倍率を掛けると二重補正になる。
const RELATIVE_UNIT_PROPERTIES = new Set([
  "text-offset",
  "text-radial-offset",
  "line-dasharray",
  "icon-offset",
]);

type MaplibreStyleValue = unknown;

const isStyleArray = (value: MaplibreStyleValue): value is unknown[] =>
  Array.isArray(value);

interface LayerSnapshot {
  id: string;
  layoutValues: ReadonlyMap<string, MaplibreStyleValue>;
  paintValues: ReadonlyMap<string, MaplibreStyleValue>;
  visibility: MaplibreStyleValue;
  minzoom?: number;
  maxzoom?: number;
}

const scaleExpressionOutput = (
  value: MaplibreStyleValue,
  printScale: number
): MaplibreStyleValue =>
  typeof value === "number" ? value * printScale : ["*", value, printScale];

const isZoomCurveExpression = (
  value: MaplibreStyleValue
): value is readonly MaplibreStyleValue[] => {
  if (!isStyleArray(value)) return false;
  const operator = value[0];
  if (operator !== "interpolate" && operator !== "step") return false;

  const input = value[operator === "interpolate" ? 2 : 1];
  return isStyleArray(input) && input[0] === "zoom";
};

const isNumericLiteralArray = (
  value: MaplibreStyleValue
): value is readonly number[] =>
  isStyleArray(value) &&
  value.length >= 1 &&
  value.length <= 4 &&
  value.every((item) => typeof item === "number");

const scaleZoomCurveExpression = (
  expression: readonly MaplibreStyleValue[],
  context: PrintRenderContext
): MaplibreStyleValue[] => {
  const isInterpolate = expression[0] === "interpolate";
  const firstStopIndex = 3;
  const scaled = isInterpolate
    ? expression.slice(0, firstStopIndex)
    : [
        ...expression.slice(0, 2),
        scaleExpressionOutput(expression[2], context.printScale),
      ];

  for (let index = firstStopIndex; index < expression.length; index += 2) {
    const stop = expression[index];
    scaled.push(
      typeof stop === "number" ? stop + context.zoomOffset : stop,
      scaleExpressionOutput(expression[index + 1], context.printScale)
    );
  }

  return scaled;
};

export const scaleMaplibrePixelValue = (
  value: MaplibreStyleValue,
  context: PrintRenderContext
): MaplibreStyleValue => {
  if (typeof value === "number") {
    return value * context.printScale;
  }
  if (isNumericLiteralArray(value)) {
    return value.map((item) => item * context.printScale);
  }
  if (isZoomCurveExpression(value)) {
    return scaleZoomCurveExpression(value, context);
  }
  if (isStyleArray(value)) {
    return ["*", value.map((item) => item), context.printScale];
  }

  return value;
};

const hasLayer = (map: MaplibreMap, id: string): boolean =>
  map.getLayer(id) != null;

const isMissingLayerError = (error: unknown): boolean =>
  error instanceof Error &&
  (/cannot style non-existing layer/i.test(error.message) ||
    /\blayer\b.*\bdoes not exist\b/i.test(error.message));

type LayerReadResult<T> =
  | { layerExists: true; value: T }
  | { layerExists: false };

const readLayerValue = <T>(
  map: MaplibreMap,
  id: string,
  read: () => T
): LayerReadResult<T> => {
  if (!hasLayer(map, id)) return { layerExists: false };

  try {
    return { layerExists: true, value: read() };
  } catch (error) {
    if (isMissingLayerError(error) && !hasLayer(map, id)) {
      return { layerExists: false };
    }
    throw error;
  }
};

const writeLayerValue = (
  map: MaplibreMap,
  id: string,
  write: () => void
): void => {
  if (!hasLayer(map, id)) return;

  try {
    write();
  } catch (error) {
    if (!isMissingLayerError(error) || hasLayer(map, id)) throw error;
  }
};

const setLayoutPropertyIfPresent = (
  map: MaplibreMap,
  id: string,
  property: string,
  value: MaplibreStyleValue
): void => {
  writeLayerValue(map, id, () => map.setLayoutProperty(id, property, value));
};

const setPaintPropertyIfPresent = (
  map: MaplibreMap,
  id: string,
  property: string,
  value: MaplibreStyleValue
): void => {
  writeLayerValue(map, id, () => map.setPaintProperty(id, property, value));
};

const setZoomRangeIfPresent = (
  map: MaplibreMap,
  id: string,
  minzoom: number | undefined,
  maxzoom: number | undefined
): void => {
  if (minzoom === undefined || maxzoom === undefined) return;

  writeLayerValue(map, id, () => map.setLayerZoomRange(id, minzoom, maxzoom));
};

const snapshotLayer = (
  map: MaplibreMap,
  definition: MaplibrePrintLayerDefinition
): LayerSnapshot | undefined => {
  const layer = map.getLayer(definition.id);
  if (layer == null) return undefined;

  const layoutValues = new Map<string, MaplibreStyleValue>();
  for (const property of definition.layoutPixelProperties ?? []) {
    if (!RELATIVE_UNIT_PROPERTIES.has(property)) {
      const result = readLayerValue(
        map,
        definition.id,
        () =>
          map.getLayoutProperty(definition.id, property) as MaplibreStyleValue
      );
      if (!result.layerExists) return undefined;
      layoutValues.set(property, result.value);
    }
  }

  const paintValues = new Map<string, MaplibreStyleValue>();
  for (const property of definition.paintPixelProperties ?? []) {
    if (!RELATIVE_UNIT_PROPERTIES.has(property)) {
      const result = readLayerValue(map, definition.id, () =>
        map.getPaintProperty(definition.id, property)
      );
      if (!result.layerExists) return undefined;
      paintValues.set(property, result.value);
    }
  }

  const visibility = readLayerValue(
    map,
    definition.id,
    () =>
      map.getLayoutProperty(definition.id, "visibility") as MaplibreStyleValue
  );
  if (!visibility.layerExists) return undefined;

  return {
    id: definition.id,
    layoutValues,
    paintValues,
    visibility: visibility.value,
    minzoom: layer.minzoom,
    maxzoom: layer.maxzoom,
  };
};

export const createMaplibrePrintAdapter = ({
  map,
  layers,
}: {
  map: MaplibreMap;
  layers: MaplibrePrintLayerDefinition[];
}): PrintLayerAdapter => {
  let snapshots: LayerSnapshot[] = [];

  return {
    id: `maplibre:${layers.map(({ id }) => id).join(",")}`,
    apply: (context) => {
      snapshots = [];
      layers.forEach((definition) => {
        const snapshot = snapshotLayer(map, definition);
        if (snapshot == null) return;
        snapshots.push(snapshot);

        snapshot.layoutValues.forEach((value, property) => {
          if (value !== undefined) {
            setLayoutPropertyIfPresent(
              map,
              definition.id,
              property,
              scaleMaplibrePixelValue(value, context)
            );
          }
        });
        snapshot.paintValues.forEach((value, property) => {
          if (value !== undefined) {
            setPaintPropertyIfPresent(
              map,
              definition.id,
              property,
              scaleMaplibrePixelValue(value, context)
            );
          }
        });
        setLayoutPropertyIfPresent(
          map,
          definition.id,
          "visibility",
          definition.visible ? "visible" : "none"
        );
        setZoomRangeIfPresent(
          map,
          definition.id,
          snapshot.minzoom === undefined
            ? undefined
            : snapshot.minzoom + context.zoomOffset,
          snapshot.maxzoom === undefined
            ? undefined
            : snapshot.maxzoom + context.zoomOffset
        );
      });
    },
    restore: () => {
      snapshots.forEach((snapshot) => {
        snapshot.layoutValues.forEach((value, property) => {
          if (value !== undefined) {
            setLayoutPropertyIfPresent(map, snapshot.id, property, value);
          }
        });
        snapshot.paintValues.forEach((value, property) => {
          if (value !== undefined) {
            setPaintPropertyIfPresent(map, snapshot.id, property, value);
          }
        });
        setLayoutPropertyIfPresent(
          map,
          snapshot.id,
          "visibility",
          snapshot.visibility
        );
        setZoomRangeIfPresent(
          map,
          snapshot.id,
          snapshot.minzoom,
          snapshot.maxzoom
        );
      });
    },
  };
};
